import { Calendar } from 'primereact/calendar';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useForm, useWatch, SubmitHandler } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import React, { useEffect, useState, useRef } from 'react';

import {
  getVehicleByVin,
  getPaymentMethods,
  getBookingCalculate,
  resetContactUs,
  addBooking,
  getProfile,
  resetBooking,
  getRecommendedVehicleByToken,
  addContactUs,
} from '../../core/data/redux/action';

import { all_routes } from '../router/all_routes';
import Breadcrumbs from '../common/breadcrumbs';
import ImageWithBasePath from '../../core/data/img/ImageWithBasePath';

import { AppDispatch, RootState } from '../../core/data/redux/store';
import { toast, ToastContainer } from 'react-toastify';
import BookingCheckout from './booking-checkout';
import BookingDetail from './booking-detail';
import BookingPayment from './booking-payment';
import {
  countryCodes,
  formatToTwoDecimals,
} from '../../core/data/redux/actions/commonActions';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import 'react-toastify/dist/ReactToastify.css';
import CarDetail from '../../core/data/modals/car_details';
import {
  generateTimeOptions,
  BookingSteps,
} from '../../core/data/redux/actions/commonActions';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import LoginModal from '../../core/data/modals/loginModal';
import dayjs from 'dayjs';
import Lottie from 'lottie-react';
import Loader from '../../core/data/img/car_loading.json';
import moment from 'moment';

import ContactConfirm from '../../core/data/modals/contactConfirm';
import EnquiryForm from '../../core/data/modals/enquiry';
import { formatDateWithTime } from '../../core/data/redux/actions/commonActions';
import { getAllTemplate } from '../../core/data/redux/action';

const BookingForm = () => {
  const [step, setStep] = useState(1);
  const { vin } = useParams();
  const popoverRef = useRef(null);
  const routes = all_routes;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [localMessage, setLocalMessage] = useState('');

  useEffect(() => {
    dispatch(getAllTemplate());

    if (localStorage.getItem('token')) {
      dispatch(getProfile());
    }

    dispatch(getPaymentMethods());
  }, [dispatch]);

  const {
    contactUsError,
    contactUsSuccess,
    contactUsLoading,
    paymentMethods,
    bookingTotalAmount,
    bookingsCalculateError,
    systemSettings,
    vehicleRecommendationData,
    vehicleRecommendationError,
    vehicleRecommendationLoading,
    user,
    carDetail,
    bookings,
    bookingsSuccess,
    bookingsError,
    success,
  } = useSelector((state: RootState) => ({
    bookingsSuccess: state.booking.bookingsSuccess,
    bookingsError: state.booking.bookingsError,
    carDetail: state.vehicle.carDetail,
    bookings: state.booking.bookings,
    user: state.auth.user,
    bookingTotalAmount: state.booking.bookingTotalAmount,
    paymentMethods: state.payment.paymentMethods,
    systemSettings: state.auth.systemSettings,
    bookingsCalculateError: state.booking.bookingsCalculateError,
    vehicleRecommendationData:
      state.vehicleRecommendation.vehicleRecommendationData,
    vehicleRecommendationError:
      state.vehicleRecommendation.vehicleRecommendationError,
    vehicleRecommendationLoading:
      state.vehicleRecommendation.vehicleRecommendationLoading,
    success: state.auth.success,
    contactUsLoading: state.contactUs.contactUsLoading,
    contactUsSuccess: state.contactUs.contactUsSuccess,
    contactUsError: state.contactUs.contactUsError,
  }));

  useEffect(() => {
    if (contactUsSuccess) {
      showToast(contactUsSuccess);

      closeModal();
      dispatch(resetContactUs());
    } else if (contactUsError) {
      showToast(contactUsError);
      dispatch(resetContactUs());
    }
  }, [contactUsSuccess, contactUsError]);

  const closeModal = () => {
    const modalElement = document.getElementById('enquiry');
    const backdrop = document.querySelector('.modal-backdrop');

    if (modalElement) {
      modalElement.style.display = 'none';
      modalElement.classList.remove('show', 'modal-open');
    }
    if (backdrop) {
      backdrop.remove();
    }

    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  };

  const closeConfirmModal = () => {
    const backdrop = document.querySelector('.modal-backdrop');
    const confirmModal = document.getElementById('confirm');

    if (confirmModal) {
      confirmModal.style.display = 'none';
      confirmModal.classList.remove('show', 'modal-open');
    }
    if (backdrop) {
      backdrop.remove();
    }
  };

  const [timeOptions, setTimeOptions] = useState<string[]>([]);

  useEffect(() => {
    if (systemSettings.booking_start_time && systemSettings.booking_end_time) {
      const options = generateTimeOptions(
        systemSettings.booking_start_time,
        systemSettings.booking_end_time,
        0
      );
      setTimeOptions(options);
    }
  }, [systemSettings.booking_start_time, systemSettings.booking_end_time]);

  type FormValues = {
    user_id: string;
    vehicle_id: string;
    pickup_location: string;
    booking_start_date: string;
    booking_return_date: string;
    booking_start_time: string;
    booking_return_time: string;
    billing_first_name: string;
    billing_last_name: string;
    billing_address: string;
    billing_city: string;
    billing_pin_code: string;
    billing_email: string;
    billing_country_code: string;
    billing_country: string;
    billing_state: string;
    billing_mobile: string;
    billing_terms_conditions: boolean;
    use_same_residency_address: boolean;
    parking_address: string;
    parking_city: string;
    parking_state: string;
    parking_pin_code: string;
    parking_country: string;
    payment_method_id: string | null;
    attachments: [];
    is_sms_alert_approved: boolean;
  };

  const getValidationSchema = (
    step: number
  ): Yup.ObjectSchema<Partial<FormValues>> => {
    return Yup.object().shape(
      step === 1
        ? {
            pickup_location: Yup.string().when('rental_type', {
              is: 'self pickup',
              then: (schema) => schema.required('Pickup location is required'),
            }),
            booking_start_date: Yup.string().required('Start date is required'),
            booking_return_date: Yup.string().required(
              'Return date is required'
            ),
            booking_start_time: Yup.string().required('Start time is required'),
            booking_return_time: Yup.string().required(
              'Return time is required'
            ),
            payment_method_id: Yup.string().required(
              'Payment method is required'
            ),
          }
        : {
            billing_first_name: Yup.string().required(
              'Billing first name is required'
            ),
            billing_last_name: Yup.string().required(
              'Billing last name is required'
            ),
            billing_address: Yup.string().required(
              'Billing address is required'
            ),
            billing_email: Yup.string()
              .email('Invalid email')
              .required('Email is required'),
            billing_country: Yup.string().required('Country is required'),

            billing_city: Yup.string().required('City is required'),
            billing_state: Yup.string().required('State is required'),
            billing_pin_code: Yup.string().required('Pin code is required'),
            billing_country_code: Yup.string().required(
              'Country code is required'
            ),
            billing_mobile: Yup.string().required('Mobile is required'),

            is_sms_alert_approved: Yup.boolean().notRequired(),

            use_same_residency_address: Yup.boolean().notRequired(),
            parking_address: Yup.string().when('use_same_residency_address', {
              is: false,
              then: (schema) => schema.required('Parking address is required'),
              otherwise: (schema) => schema.notRequired(),
            }),
            parking_city: Yup.string().when('use_same_residency_address', {
              is: false,
              then: (schema) => schema.required('Parking city is required'),
              otherwise: (schema) => schema.notRequired(),
            }),
            parking_state: Yup.string().when('use_same_residency_address', {
              is: false,
              then: (schema) => schema.required('Parking state is required'),
              otherwise: (schema) => schema.notRequired(),
            }),
            parking_pin_code: Yup.string().when('use_same_residency_address', {
              is: false,
              then: (schema) => schema.required('Parking zip is required'),
              otherwise: (schema) => schema.notRequired(),
            }),
            parking_country: Yup.string().when('use_same_residency_address', {
              is: false,
              then: (schema) => schema.required('Parking country is required'),
              otherwise: (schema) => schema.notRequired(),
            }),

            billing_terms_conditions: Yup.boolean().oneOf(
              [true],
              'Accept terms & conditions is required'
            ),

            attachments: Yup.array()
              .of(
                Yup.object().shape({
                  label: Yup.string().required('Attachment label is required'),
                  type: Yup.string().required('Attachment type is required'),
                })
              )
              .min(1, 'At least one attachment is required')
              .required('Attachments are required')
              .test(
                'required-labels-types',
                'Attachments must include: driving_license (upload), address_proof (upload)',
                function (attachments) {
                  const requiredAttachments = [
                    { label: 'driving_license', type: 'upload' },
                    { label: 'address_proof', type: 'upload' },
                    { label: 'driving_license', type: 'selfie' },
                  ];
                  const hasAllRequired = requiredAttachments.every((required) =>
                    attachments?.some(
                      (att) =>
                        att.label?.toLowerCase() === required.label &&
                        att.type?.toLowerCase() === required.type
                    )
                  );

                  return hasAllRequired;
                }
              ),
          }
    );
  };

  const nextStep = async () => {
    setOpen(false);

    if (user?.id) {
      const isValid = await trigger();

      if (isValid && bookingTotalAmount?.finalEstimate > 0) {
        window.scrollTo(0, 0);
        setStep((prev) => prev + 1);
      }
    }
  };

  const prevStep = () => {
    setOpen(false);
    window.scrollTo(0, 0);
    setStep((prev) => prev - 1);
  };

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const getParam = (key: string) => {
    const value = queryParams.get(key);
    return value ? decodeURIComponent(value) : '';
  };
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(
    tomorrow.getDate() + parseInt(systemSettings.booking_min_duration ?? 7)
  );

  const [startDate, setStartDate] = useState<Date | null>(
    getParam('pickupDate') ? dayjs(getParam('pickupDate'))?.toDate() : today
  );

  const [endDate, setEndDate] = useState<Date | null>(
    getParam('pickupTime') ? dayjs(getParam('returnDate'))?.toDate() : tomorrow
  );

  const [fromTime, setFromTime] = useState(
    getParam('pickupTime') ?? systemSettings?.booking_start_time
  );
  const [untilTime, setUntilTime] = useState(
    getParam('returnTime') ?? systemSettings?.booking_end_time
  );

  type ContactFormValues = {
    name: string;
    email: string;
    message: string;
    phone: string;
    is_sms_alert_approved: boolean;
  };

  const ContactSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    message: Yup.string().required('Message is required'),
    phone: Yup.string().when('$phone', {
      is: (value: any) => value?.trim().length > 0, // Check if phone is not empty
      then: (schema) => schema.length(10, 'Phone must be exactly 10 digits'),
      otherwise: (schema) => schema.notRequired(), // Don't validate if empty
    }),
    is_sms_alert_approved: Yup.boolean().notRequired(),
  });

  const {
    register: contactRegister,
    handleSubmit: contactHandleSubmit,
    formState: { errors: contactErrors },
    reset: contactReset,
  } = useForm<ContactFormValues>({
    resolver: yupResolver(ContactSchema),
    defaultValues: {
      name: user?.name,
      email: user?.email,
      message: '',
      phone: user?.mobile,
      is_sms_alert_approved: false,
    },
  });

  const contactOnSubmit: SubmitHandler<ContactFormValues> = (data) => {
    const currentUrl = `${window.location.origin}${location.pathname}${location.search}`;

    dispatch(
      addContactUs({
        ...data,
        is_sms_alert_approved: data?.is_sms_alert_approved ? 1 : 0,
        vehicle_id: carDetail?.id,
        query_type: 'enquiry',
        fairental_message: `Hi Fairental team, we have a car enquiry from a customer ${data?.name}, ${data.phone}, ${data.email}, Enquiry :${data.message}, please click on the link to see the car details: ${currentUrl}`,
      })
    );

    const encodedMessage = encodeURIComponent(
      `Hi Fairental team, we have a car enquiry from a customer ${data?.name}, ${data.phone}, ${data.email}, Enquiry :${data.message}, please click on the link to see the car details: ${currentUrl}`
    );

    const value = {
      encodedMessage: encodedMessage,
      timestamp: new Date().getTime(),
      currentUrl: currentUrl,
    };

    sessionStorage.setItem('contactForm', JSON.stringify(value));
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitted },
    watch,
    setValue,
    trigger,
    control,
  } = useForm<FormValues>({
    resolver: yupResolver(getValidationSchema(step)),
    defaultValues: {
      user_id: user?.id,
      vehicle_id: carDetail?.id,
      pickup_location: systemSettings?.default_pickup_location,
      booking_start_date: '',
      booking_return_date: '',
      booking_start_time: '',
      booking_return_time: '',
      payment_method_id: '',
      billing_first_name: '',
      billing_last_name: '',
      billing_address: '',
      billing_country: 'USA',
      billing_city: '',
      billing_state: '',
      billing_pin_code: '',
      billing_email: '',
      billing_country_code: '+1',
      billing_mobile: '',
      billing_terms_conditions: false,
      use_same_residency_address: false,
      parking_address: '',
      parking_city: '',
      parking_state: '',
      parking_pin_code: '',
      parking_country: 'USA',
      attachments: undefined,
    },
  }); 

  useEffect(() => {
    if (user) {
      contactReset({
        name: user.name ?? '',
        email: user.email ?? '',
        phone: user.mobile ?? '',
        message: '',
      });
    }
  }, [user, contactReset, contactUsSuccess]);

  const bookingStartDate = useWatch({ control, name: 'booking_start_date' });
  const bookingReturnDate = useWatch({ control, name: 'booking_return_date' });
  const bookingStartTime = useWatch({ control, name: 'booking_start_time' });
  const bookingReturnTime = useWatch({ control, name: 'booking_return_time' });
  const paymentMethodId = useWatch({ control, name: 'payment_method_id' });

  useEffect(() => {
    if (getParam('token') && vehicleRecommendationData?.bookingInfo) {
      if (vehicleRecommendationData?.bookingInfo) {
        setValue(
          'booking_start_date',
          vehicleRecommendationData?.bookingInfo?.start_date ?? ''
        );
        setValue(
          'booking_return_date',
          vehicleRecommendationData?.bookingInfo?.return_date ?? ''
        );
        setValue(
          'booking_start_time',
          vehicleRecommendationData?.bookingInfo?.start_time ?? ''
        );
        setValue(
          'booking_return_time',
          vehicleRecommendationData?.bookingInfo?.return_time ?? ''
        );
        setValue(
          'payment_method_id',
          vehicleRecommendationData?.bookingInfo?.payment_method_id ?? ''
        );

        if (vehicleRecommendationData?.bookingInfo?.status === 'booked') {
          const bookingId = vehicleRecommendationData?.bookingInfo?.booking_id;
          const licensePending = vehicleRecommendationData?.licensePending;
          const paymentPending = vehicleRecommendationData?.paymentPending;

          let query = `?bookingId=${bookingId}`;
          query += `&licensePending=${licensePending}`;
          query += `&paymentPending=${paymentPending}`;

          navigate(routes?.userBookings + query);
        }
      }
    } else {
      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(
        today.getDate() + systemSettings?.booking_min_duration
      );

      setValue(
        'booking_start_date',
        getParam('pickupDate') ?? moment(today, 'YYYY-MM-DD')
      );
      setValue(
        'booking_return_date',
        getParam('returnDate') ?? moment(sevenDaysAgo, 'YYYY-MM-DD')
      );
      setValue(
        'booking_start_time',
        getParam('pickupTime')
          ? getParam('pickupTime')
          : systemSettings?.booking_start_time
      );
      setValue(
        'booking_return_time',
        getParam('returnTime')
          ? getParam('returnTime')
          : systemSettings?.booking_end_time
      );
    }
  }, [vehicleRecommendationData, success]);

  const isValidTime = (timeStr: string) => {
    const parsed = moment(timeStr, 'h:mm A');
    return parsed.isValid();
  };

  useEffect(() => {
    if (
      vin &&
      bookingStartDate &&
      bookingReturnDate &&
      isValidTime(bookingStartTime) &&
      isValidTime(bookingReturnTime)
    ) {
      dispatch(
        getVehicleByVin({
          vin: vin,
          from_date: formatDateWithTime(
            new Date(bookingStartDate),
            bookingReturnTime
          ),
          to_date: formatDateWithTime(endDate, bookingReturnTime),
        })
      );
    }
  }, [
    vin,
    bookingStartDate,
    bookingReturnDate,
    bookingStartTime,
    bookingReturnTime,
  ]);

  useEffect(() => {
    if (vehicleRecommendationError) {
      if (localStorage.getItem('token')) {
        navigate(routes.userBookings);
      } else {
        const loginModal = document.getElementById('login');

        if (loginModal) {
          loginModal.style.display = 'block';
          loginModal.classList.add('show');
        }

        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop fade show';
        document.body.appendChild(backdrop);
        document.body.classList.add('modal-open');
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = '0px';
      }
    }
  }, [vehicleRecommendationError]);

  useEffect(() => {
    if (getParam('token')) {
      dispatch(
        getRecommendedVehicleByToken(vin?.toString(), getParam('token'))
      );
    } else if (getParam('pickupDate') && getParam('returnDate')) {
      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(
        today.getDate() + systemSettings?.booking_min_duration
      );

      setValue(
        'booking_start_date',
        getParam('pickupDate') ?? moment(today, 'YYYY-MM-DD')
      );
      setValue(
        'booking_return_date',
        getParam('returnDate') ?? moment(sevenDaysAgo, 'YYYY-MM-DD')
      );
      setValue(
        'booking_start_time',
        getParam('pickupTime')
          ? getParam('pickupTime')
          : systemSettings?.booking_start_time
      );
      setValue(
        'booking_return_time',
        getParam('returnTime')
          ? getParam('returnTime')
          : systemSettings?.booking_end_time
      );
    }
  }, [vin, success]);

  useEffect(() => {
    if (success) {
      const backdrop = document.querySelector('.modal-backdrop');
      const loginModal = document.getElementById('login');

      if (loginModal) {
        loginModal.style.display = 'none';
        loginModal.classList.remove('show', 'modal-open');
      }

      if (backdrop) {
        backdrop.remove();
      }

      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  }, [success]);

  useEffect(() => {
    const data = sessionStorage.getItem('contactForm');

    if (data) {
      const parsedData = JSON.parse(data);
      const now = new Date().getTime();
      const oneHour = 60 * 60 * 1000;

      if (now - parsedData.timestamp > oneHour) {
        sessionStorage.removeItem('contactForm'); // Remove if expired
      }

      if (
        parsedData.currentUrl ===
        `${window.location.origin}${location.pathname}${location.search}`
      ) {
        setLocalMessage(parsedData.encodedMessage);
      }
    } else {
      setLocalMessage('');
    }
  }, [sessionStorage.getItem('contactForm')]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const formData = new FormData();

    if (getParam('token')) {
      formData.append('token', getParam('token'));
    }

    formData.append(
      'booking_start_time',
      data['booking_start_time']
        ? moment(data['booking_start_time'], ['h:mm A', 'HH:mm:ss']).format(
            'HH:mm'
          )
        : ''
    );

    formData.append(
      'booking_return_time',
      data['booking_return_time']
        ? moment(data['booking_return_time'], ['h:mm A', 'HH:mm:ss']).format(
            'HH:mm'
          )
        : ''
    );

    formData.append(
      'booking_return_date',
      data['booking_return_date']
        ? moment(data['booking_return_date']).format('YYYY-MM-DD')
        : ''
    );

    formData.append(
      'booking_start_date',
      data['booking_start_date']
        ? moment(data['booking_start_date']).format('YYYY-MM-DD')
        : ''
    );

    formData.append('user_id', user?.id);
    formData.append('vehicle_id', carDetail?.id);
    formData.append('payment_method_id', paymentMethodId ?? '');

    formData.append(
      'is_sms_alert_approved',
      data['is_sms_alert_approved'] ? '1' : '0'
    );

    formData.append(
      'use_same_residency_address',
      data['use_same_residency_address'] ? '1' : '0'
    );

    if (!data['use_same_residency_address']) {
      formData.append('parking_address', data['parking_address'] ?? '');
      formData.append('parking_city', data['parking_city'] ?? '');
      formData.append('parking_state', data['parking_state'] ?? '');
      formData.append('parking_pin_code', data['parking_pin_code'] ?? '');
      formData.append('parking_country', data['parking_country'] ?? '');
    }

    Object.entries(data).forEach(([key, value]) => {
      if (
        key !== 'use_same_residency_address' &&
        key !== 'is_sms_alert_approved' &&
        key !== 'attachments' &&
        key !== 'booking_start_time' &&
        key !== 'booking_return_time' &&
        key !== 'booking_start_date' &&
        key !== 'booking_return_date' &&
        // If same residency address is used, do not send any parking_* fields
        !(data['use_same_residency_address'] && key.startsWith('parking_'))
      ) {
        if (typeof value === 'boolean') {
          formData.append(key, value.toString());
        } else if (typeof value === 'string' || value instanceof Blob) {
          formData.append(key, value);
        }
      }
    });

    formData.append(
      'license_uploaded',
      data.attachments.filter(
        (att: any) => att.label === 'driving_license' && att.type === 'upload'
      )?.length > 0
        ? '0'
        : '1'
    );

    if (
      data?.attachments &&
      Array.isArray(data.attachments.filter((att: any) => att.label !== ''))
    ) {
      data.attachments.forEach((attachment: any, index: any) => {
        if (attachment?.file !== undefined) {
          formData.append(`attachments[${index}][file]`, attachment.file);
          formData.append(`attachments[${index}][label]`, attachment.label);
          formData.append(`attachments[${index}][type]`, attachment.type);
        }
      });
    }

    if (formData) {
      if (Object.entries(errors)?.length === 0) {
        const isValid = await trigger();
        if (isValid && bookingTotalAmount?.finalEstimate > 0) {
          dispatch(addBooking(formData));
        }
      }
    }
  };

  useEffect(() => {
    if (bookingsSuccess) {
      window.scrollTo(0, 0);
      setStep((prev) => prev + 1);
      dispatch(resetBooking());
    }
  }, [bookingsSuccess, bookingsError, bookingsCalculateError]);

  useEffect(() => {
    if (user) {
      setValue('user_id', user?.id);
      setValue('billing_first_name', user.first_name ?? '');
      setValue('billing_last_name', user.last_name ?? '');
      setValue('billing_country_code', user.country_code ?? '+1');
      setValue('billing_mobile', user.mobile ?? '');
      setValue('billing_email', user.email ?? '');
      setValue('billing_address', user.address ?? '');
      setValue('billing_city', user.city ?? '');
      setValue('billing_state', user.state ?? '');
      setValue('billing_pin_code', user.zip ?? '');
      setValue(
        'billing_country',
        countryCodes.find((item) => item?.code === user?.country_code)
          ?.country ?? 'USA'
      );
    }
  }, [user]);

  useEffect(() => {
    if (paymentMethods?.[0]?.id) {
      setValue('payment_method_id', paymentMethods?.[0]?.id);
    }
  }, [paymentMethods, setValue]);

  useEffect(() => {
    if (
      carDetail?.id &&
      bookingStartDate &&
      bookingReturnDate &&
      isValidTime(bookingStartTime) &&
      isValidTime(bookingReturnTime) &&
      paymentMethodId
    ) {
      dispatch(
        getBookingCalculate({
          vehicle_id: carDetail?.id,
          start_date:
            dayjs(bookingStartDate).format('YYYY-MM-DD') +
            ' ' +
            bookingStartTime,
          end_date:
            dayjs(bookingReturnDate).format('YYYY-MM-DD') +
            ' ' +
            bookingReturnTime,
          payment_method_id: paymentMethodId,
          token: getParam('token'),
        })
      );

      setValue('vehicle_id', carDetail?.id);
    }
  }, [
    carDetail?.id,
    bookingStartDate,
    bookingReturnDate,
    bookingReturnTime,
    bookingStartTime,
    dispatch,
    setValue,
    paymentMethodId,
  ]);

  const [toastMessage, setToastMessage] = useState('');
  const [toastClass, setToastClass] = useState('bg-success text-white');

  useEffect(() => {
    if (bookingsSuccess) {
      showToast(bookingsSuccess);
    } else if (bookingsError) {
      showToast(bookingsError);
    } else if (bookingsCalculateError) {
      showToast(bookingsCalculateError);
    }
  }, [bookingsSuccess, bookingsError, bookingsCalculateError]);

  const showToast = (message: any) => {
    setToastClass(
      bookingsError || bookingsCalculateError
        ? 'bg-danger text-white'
        : 'bg-success text-white'
    );
    setToastMessage(message);
  };

  const handleCloseToast = () => {
    setToastMessage('');
    dispatch(resetBooking());
  };

  const [isMobile] = useState(window.innerWidth <= 767);

  useEffect(() => {
    if (toastMessage) {
      if (toastClass === 'bg-danger text-white') {
        toast.error(toastMessage, {
          position: 'top-right',
          hideProgressBar: true,
          onClose: handleCloseToast,
          progress: undefined,
          toastId: '',
        });
      } else {
        toast.success(toastMessage, {
          position: 'top-right',
          hideProgressBar: true,
          onClose: handleCloseToast,
          progress: undefined,
          toastId: '',
        });
      }
    }
  }, [
    toastMessage,
    toastClass,
    bookingsSuccess,
    bookingsError,
    bookingsCalculateError,
  ]);

  return (
    <div className="booking-new-module mb-3">
      <Breadcrumbs title={`${carDetail.vehicle_name}`} subtitle="Checkout" />

      <div className="container">
        {vehicleRecommendationLoading ? (
          <>
            <div className="p-5 m-5 d-flex justify-content-center align-items-center">
              <Lottie
                animationData={Loader}
                loop={true}
                style={{ width: 300, height: 300 }}
              />
            </div>
          </>
        ) : (
          <>
            <div className="booking-wizard-head px-0">
              <div className="row align-items-center">
                <div className="col-xl-4 col-lg-3">
                  <div className="booking-head-title d-none d-md-block">
                    <h4>Reserve Your Car</h4>
                  </div>
                </div>
                <div className="col-xl-8 col-lg-9 justify-content-left display-grid">
                  <Box className="px-0 " sx={{ width: '100%' }}>
                    <Stepper
                      activeStep={step}
                      alternativeLabel
                      sx={{
                        '.MuiStep-root': { width: '5rem' },
                        '.MuiStepIcon-root.Mui-completed': { color: '#009FDF' },
                        '.MuiStepIcon-root.Mui-active': { color: '#009FDF' },
                        '.MuiStepLabel-label': {
                          fontFamily: 'GT Walsheim Trial Rg',
                        },
                      }}
                    >
                      {BookingSteps?.map((label) => (
                        <Step key={label}>
                          <StepLabel>{label}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>
                </div>
              </div>
            </div>
            <div className="booking-detail-info">
              <div className="row">
                <div className="col-lg-4 theiaStickySidebar">
                  <div className="stickybar">
                    <div className="booking-sidebar">
                      <div className="booking-sidebar-card mb-0 shadow-none">
                        <div className="booking-sidebar-head d-none d-md-block">
                          <h5 className="">Car Details</h5>
                        </div>
                        <div className="booking-sidebar-body">
                          <div className="booking-car-detail px-0 pt-0 py-1">
                            <span
                              className="car-img"
                              data-bs-toggle="modal"
                              data-bs-target="#car_details"
                            >
                              <ImageWithBasePath
                                src={`${carDetail.vehicle_image}`}
                                className="img-fluid"
                                alt="Car"
                              />
                            </span>
                            <div className="care-more-info">
                              <h6 className="fw-bold">
                                {carDetail?.vehicle_name}{' '}
                              </h6>
                              <p className="mb-1">
                                {systemSettings?.default_pickup_location}
                              </p>

                              <p
                                onClick={() => {
                                  if (
                                    step !== 3 &&
                                    getParam('token') === '' &&
                                    isMobile
                                  ) {
                                    setOpen(!open);
                                  } else {
                                    return;
                                  }
                                }}
                              >
                                {watch('booking_start_date') &&
                                watch('booking_return_date')
                                  ? `${dayjs(watch('booking_start_date')).format('MMM DD')}  ${watch('booking_start_time')} -
                                                   ${dayjs(watch('booking_return_date')).format('MMM DD')} ${watch('booking_return_time')}`
                                  : 'Select Date'}
                              </p>

                              {open && (
                                <div
                                  ref={popoverRef}
                                  className="bg-white position-absolute"
                                  style={{
                                    width: '100%',
                                    boxShadow:
                                      '0px 4px 10px rgba(0, 0, 0, 0.15)',
                                    borderRadius: '8px',
                                    zIndex: 99,
                                    left: '0rem',
                                  }}
                                >
                                  <div className="w-100 p-2">
                                    <span
                                      className="d-flex float-end text-end"
                                      onClick={() => {
                                        setOpen(!open);
                                      }}
                                    >
                                      {' '}
                                      <i className="feather icon-x fs-5 " />
                                    </span>

                                    <div className="mt-1">
                                      <div className="">
                                        <label
                                          htmlFor="calendar-12h "
                                          className="explore-text block mb-0"
                                        >
                                          {' '}
                                          From{' '}
                                        </label>

                                        <div className="d-flex gap-2">
                                          <Calendar
                                            minDate={new Date()}
                                            className="datetimepicker"
                                            value={
                                              watch('booking_start_date')
                                                ? dayjs(
                                                    watch('booking_start_date')
                                                  )?.toDate()
                                                : null
                                            } // Ensure it's a Date object
                                            onChange={(e) => {
                                              setValue(
                                                'booking_start_date',
                                                e.target.value,
                                                { shouldValidate: true }
                                              );

                                              const date = e.value as Date;
                                              const time =
                                                watch('booking_start_time'); // Could be "08:00" or "8:00 AM"
                                              const duration = parseInt(
                                                systemSettings?.booking_min_duration ||
                                                  '0'
                                              );

                                              if (
                                                date &&
                                                typeof time === 'string' &&
                                                duration > 0
                                              ) {
                                                // Normalize date string
                                                const dateString = `${date.getFullYear()}-${(
                                                  date.getMonth() + 1
                                                )
                                                  .toString()
                                                  .padStart(
                                                    2,
                                                    '0'
                                                  )}-${date.getDate().toString().padStart(2, '0')}`;

                                                let hours = 0;
                                                let minutes = 0;

                                                const match24 =
                                                  time.match(
                                                    /^(\d{1,2}):(\d{2})$/
                                                  );

                                                const match12 = time.match(
                                                  /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i
                                                );

                                                if (match24) {
                                                  hours = parseInt(match24[1]);
                                                  minutes = parseInt(
                                                    match24[2]
                                                  );
                                                } else if (match12) {
                                                  hours = parseInt(match12[1]);
                                                  minutes = parseInt(
                                                    match12[2]
                                                  );
                                                  const period =
                                                    match12[3].toUpperCase();
                                                  if (
                                                    period === 'PM' &&
                                                    hours !== 12
                                                  )
                                                    hours += 12;
                                                  if (
                                                    period === 'AM' &&
                                                    hours === 12
                                                  )
                                                    hours = 0;
                                                } else {
                                                  console.warn(
                                                    'Time format not supported:',
                                                    time
                                                  );
                                                  return;
                                                }

                                                const startDateTime = new Date(
                                                  dateString
                                                );
                                                startDateTime.setHours(
                                                  hours,
                                                  minutes,
                                                  0,
                                                  0
                                                );

                                                if (
                                                  isNaN(startDateTime.getTime())
                                                ) {
                                                  console.error(
                                                    'Invalid startDateTime:',
                                                    startDateTime
                                                  );
                                                  return;
                                                }

                                                const endDateTime = new Date(
                                                  startDateTime
                                                );
                                                endDateTime.setDate(
                                                  endDateTime.getDate() +
                                                    duration
                                                );

                                                setValue(
                                                  'booking_return_date',
                                                  moment(endDateTime).format(
                                                    'YYYY-MM-DD'
                                                  ),
                                                  {
                                                    shouldValidate: true,
                                                  }
                                                );
                                              } else {
                                                console.warn(
                                                  'Date or time invalid — skipping date computation.'
                                                );
                                              }
                                            }}
                                            onHide={() =>
                                              setShowCalendar(false)
                                            }
                                            visible={showCalendar}
                                            readOnlyInput
                                          />
                                          <select
                                            value={watch('booking_start_time')}
                                            onChange={(e) => {
                                              setValue(
                                                'booking_start_time',
                                                e.target.value,
                                                { shouldValidate: true }
                                              );

                                              setValue(
                                                'booking_return_time',
                                                e.target.value,
                                                { shouldValidate: true }
                                              );
                                            }}
                                            className="form-select dynamic-select w-50   px-2"
                                          >
                                            {timeOptions.map((time, index) => (
                                              <option key={index} value={time}>
                                                {time}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                      <div className="">
                                        <label
                                          htmlFor="calendar-12h"
                                          className="explore-text   block mb-0"
                                        >
                                          {' '}
                                          Until{' '}
                                        </label>
                                        <div className="d-flex gap-2">
                                          <Calendar
                                            className="datetimepicker "
                                            value={
                                              watch('booking_return_date')
                                                ? dayjs(
                                                    watch('booking_return_date')
                                                  )?.toDate()
                                                : null
                                            } // Ensure it's a Date object
                                            onChange={(e) => {
                                              setValue(
                                                'booking_return_date',
                                                e.target.value,
                                                { shouldValidate: true }
                                              );
                                            }}
                                            onHide={() =>
                                              setShowCalendar(false)
                                            }
                                            visible={showCalendar}
                                            readOnlyInput
                                          />

                                          <select
                                            value={watch('booking_return_time')}
                                            onChange={(e) => {
                                              setValue(
                                                'booking_return_time',
                                                e.target.value,
                                                { shouldValidate: true }
                                              );
                                            }}
                                            className="form-select dynamic-select w-50   px-2"
                                          >
                                            {timeOptions.map((time, index) => (
                                              <option key={index} value={time}>
                                                {time}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {step < 3 && (
                            <div className="booking-sidebar-card shadow-none mb-0">
                              <div className="booking-sidebar-head d-none d-md-block">
                                <h5>Payment Frequency </h5>
                              </div>
                              <div className="booking-sidebar-body">
                                <div className="payment-method-list">
                                  <div className="row align-items-center ">
                                    <div className="d-flex d-md-none col-3 ps-1 ">
                                      <h6 className="text-nowrap">Pay</h6>
                                    </div>
                                    {paymentMethods?.map(
                                      (method: any, index: number) => (
                                        <div
                                          key={index}
                                          className={`col-3 col-md-4  px-0 d-flex ${index === 0 ? 'justify-content-start' : index + 1 === paymentMethods.length ? 'ms-auto justify-content-end pe-1' : 'justify-content-center'}`}
                                        >
                                          <div className="modal-form-group">
                                            <label className="custom_radio">
                                              <input
                                                type="radio"
                                                className="rent-types"
                                                name="payment_method_id"
                                                id={`payment-${index}`}
                                                checked={
                                                  paymentMethodId === method.id
                                                }
                                                onChange={() =>
                                                  setValue(
                                                    'payment_method_id',
                                                    method.id
                                                  )
                                                }
                                              />
                                              <span className="checkmark sidebar_checkmark" />
                                              <span>
                                                <p className="text-center mb-0">
                                                  {method.name}
                                                </p>
                                              </span>
                                            </label>
                                          </div>
                                        </div>
                                      )
                                    )}
                                  </div>

                                  <div className="row">
                                    <p className="fst-italic fs-12 px-1 text-justify mb-0">
                                      Book for any duration but make
                                      micropayments everyday or weekly.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="accord-list">
                            <div className="accordion" id="accordionMain1">
                              <div className="card-header-new" id="headingOne">
                                <h6 className="filter-title py-0 my-0">
                                  <a
                                    className="w-100 align-items-center"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#collapseOne"
                                    aria-expanded="true"
                                    aria-controls="collapseOne"
                                  >
                                    <span>
                                      {' '}
                                      Upfront payment
                                      <i className="p-3 fa-solid fa-chevron-up" />
                                    </span>
                                    <span className="float-end  d-flex gap-1">
                                      <p className="mb-0">
                                        $
                                        {formatToTwoDecimals(
                                          bookingTotalAmount?.initialPayment
                                        )}{' '}
                                      </p>
                                    </span>
                                  </a>
                                </h6>
                              </div>

                              <div
                                id="collapseOne"
                                className="collapse show"
                                aria-labelledby="headingOne"
                                data-bs-parent="#accordionExample1"
                              >
                                <div className="card-body-chat">
                                  <div className="row">
                                    <div className="col-md-12">
                                      <div className="selectBox-cont ps-3">
                                        <label className="d-flex justify-content-between align-items center ">
                                          <h6 className="float-end mb-1">
                                            {' '}
                                            Rental Cost (
                                            {bookingTotalAmount?.daysCount ??
                                              7}{' '}
                                            Days @ $
                                            {formatToTwoDecimals(
                                              bookingTotalAmount?.pricePerDay ??
                                                1
                                            )}
                                            /day){' '}
                                          </h6>
                                          <h5 className="float-end">
                                            $
                                            {formatToTwoDecimals(
                                              bookingTotalAmount?.initialRentalCost ??
                                                0
                                            )}
                                          </h5>
                                        </label>

                                        {bookingTotalAmount?.feeTypes?.map(
                                          (item: any, index: any) => (
                                            <div
                                              key={index}
                                              className="d-flex flex-column align-items center"
                                            >
                                              {/* Main Fee Type */}
                                              <div className="d-flex justify-content-between align-items-center">
                                                <h6 className="float-end mb-1">
                                                  {item.fee_type}
                                                </h6>
                                                <h5 className="float-end mb-1">
                                                  $
                                                  {formatToTwoDecimals(
                                                    item.calcAmount
                                                  )}
                                                </h5>
                                              </div>

                                              {/* Sub Items */}
                                              {item.subItems?.length > 0 && (
                                                <div className="ms-3">
                                                  {item.subItems?.map(
                                                    (
                                                      subItem: any,
                                                      subIndex: any
                                                    ) => (
                                                      <div
                                                        key={subIndex}
                                                        className="d-flex justify-content-between align-items-center"
                                                      >
                                                        <h6 className="text-muted mb-1">
                                                          {subItem.fee_type}
                                                        </h6>
                                                        <h5 className="text-muted mb-1">
                                                          ($
                                                          {formatToTwoDecimals(
                                                            subItem.calcAmount
                                                          )}
                                                          )
                                                        </h5>
                                                      </div>
                                                    )
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {step === 1 && (
                            <div className="row d-flex d-md-none py-0">
                              <div className=" py-3">
                                <>
                                  {user?.id ? (
                                    bookingTotalAmount?.total && (
                                      <button
                                        type="submit"
                                        onClick={nextStep}
                                        // disabled={!!error}
                                        className="btn btn-primary w-100 continue-book-btn  "
                                      >
                                        Continue Booking
                                      </button>
                                    )
                                  ) : (
                                    <button
                                      type="button"
                                      data-bs-toggle="modal"
                                      data-bs-target="#login"
                                      className="btn btn-primary w-100 continue-book-btn "
                                    >
                                      Continue Booking
                                    </button>
                                  )}

                                  {localMessage ? (
                                    <Link
                                      to="#"
                                      className="btn btn-secondary w-100 mt-1"
                                      style={{
                                        cursor: 'not-allowed',
                                        pointerEvents: 'none',
                                        opacity: 0.6,
                                      }}
                                    >
                                      Thanks! We will get back to soon
                                    </Link>
                                  ) : (
                                    <Link
                                      to="#"
                                      data-bs-toggle="modal"
                                      data-bs-target="#enquiry"
                                      className="btn btn-secondary w-100 mt-1"
                                    >
                                      Check Availability
                                    </Link>
                                  )}
                                </>
                              </div>
                            </div>
                          )}

                          <div className="row d-none d-md-flex py-0">
                            {localMessage ? (
                              <Link
                                to="#"
                                className="btn btn-secondary w-100"
                                style={{
                                  cursor: 'not-allowed',
                                  pointerEvents: 'none',
                                  opacity: 0.6,
                                }}
                              >
                                Thanks! We will get back to soon
                              </Link>
                            ) : (
                              <Link
                                to="#"
                                data-bs-toggle="modal"
                                data-bs-target="#enquiry"
                                className="btn btn-secondary w-100 d-none d-md-block"
                              >
                                Check Availability
                              </Link>
                            )}
                          </div>

                          {bookingTotalAmount?.nextBillingCycle &&
                            (paymentMethodId?.toString() === '1' ||
                              paymentMethodId?.toString() === '2') && (
                              <p className="mt-1">
                                {paymentMethodId?.toString() === '1' &&
                                bookingTotalAmount?.nextBillingCycle
                                  ?.payment_amount ? (
                                  <>
                                    {' '}
                                    Your next daily payment of{' '}
                                    <span className="fw-bold">
                                      {' '}
                                      $
                                      {formatToTwoDecimals(
                                        bookingTotalAmount?.nextBillingCycle
                                          ?.payment_amount
                                      )}
                                    </span>{' '}
                                    is due starting{' '}
                                    {dayjs(bookingStartDate).format('MMM DD')}{' '}
                                    until{' '}
                                    {dayjs(bookingReturnDate).format('MMM DD')}
                                  </>
                                ) : null}

                                {paymentMethodId?.toString() === '2' &&
                                bookingTotalAmount?.nextBillingCycle
                                  ?.payment_amount ? (
                                  <>
                                    {' '}
                                    Your next weekly payment of{' '}
                                    <span className="fw-bold">
                                      {' '}
                                      $
                                      {formatToTwoDecimals(
                                        bookingTotalAmount?.nextBillingCycle
                                          ?.payment_amount
                                      )}
                                    </span>{' '}
                                    is due starting{' '}
                                    {dayjs(bookingStartDate).format('MMM DD')}{' '}
                                    until{' '}
                                    {dayjs(bookingReturnDate).format('MMM DD')}
                                  </>
                                ) : (
                                  ''
                                )}
                              </p>
                            )}

                          <p>
                            {' '}
                            {
                              bookingTotalAmount?.feeTypes?.find(
                                (item: any) =>
                                  item.fee_type === 'Security Deposit'
                              )?.reason
                            }{' '}
                          </p>

                          {step === 2 && (
                            <h6
                              className="align-items-center mb-1 d-block d-md-none"
                              style={{ color: '#111' }}
                            >
                              {' '}
                              Please complete the details below:{' '}
                            </h6>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-8  ">
                  <div className="booking-information-main">
                    <form onSubmit={handleSubmit(onSubmit)}>
                      {step === 1 && (
                        <BookingCheckout
                          {...{
                            register,
                            errors,
                            setValue,
                            watch,
                            nextStep,
                            isSubmitted,
                          }}
                        />
                      )}
                      {step === 2 && (
                        <BookingDetail
                          {...{
                            register,
                            errors,
                            watch,
                            prevStep,
                            isSubmitted,
                            setValue,
                          }}
                        />
                      )}
                      {step === 3 && (
                        <BookingPayment {...{ bookings }} enquiry={'show'} />
                      )}
                    </form>
                  </div>
                </div>

                <LoginModal />
                <CarDetail />
              </div>
            </div>
          </>
        )}

        <EnquiryForm
          carDetail={carDetail}
          handleSubmit={contactHandleSubmit}
          contactUsLoading={contactUsLoading}
          onSubmit={contactOnSubmit}
          register={contactRegister}
          errors={contactErrors}
          timeOptions={timeOptions}
          systemSettings={systemSettings}
          setShowCalendar={setShowCalendar}
          showCalendar={showCalendar}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          setFromTime={setFromTime}
          setUntilTime={setUntilTime}
          startDate={startDate}
          endDate={endDate}
          fromTime={fromTime}
          untilTime={untilTime}
        />

        <ContactConfirm closeConfirmModal={closeConfirmModal} />

        {toastMessage && <ToastContainer autoClose={3000} limit={1} />}
      </div>
    </div>
  );
};

export default BookingForm;
